import { type Booking, BookingStatus, type Location, LocationKind, MeetingMode, type ReminderInfo } from '../../models/Scheduling'

const TIME_FORMATTER = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
const WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-US', { weekday: 'short' })
const BOOKING_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const INVITE_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' })

// "APR 24, 2026" — the uppercase date shown in a booking row's time block
export const formatBookingDate = (date: Date): string => BOOKING_DATE_FORMATTER.format(date).toUpperCase()

// "Apr 24, 2026" — the (always year-qualified) date under the Waiting "sent ago" label
export const formatSentDate = (date: Date): string => BOOKING_DATE_FORMATTER.format(date)

// Split the formatted time into its clock value and day period via formatToParts, so we never
// depend on the literal separator Intl emits (recent ICU uses a narrow no-break space, not ' ').
const formatTimeParts = (date: Date): { time: string; period: string } => {
  const parts = TIME_FORMATTER.formatToParts(date)
  const time = parts
    .filter((part) => part.type === 'hour' || part.type === 'minute' || (part.type === 'literal' && part.value === ':'))
    .map((part) => part.value)
    .join('')
  const period = parts.find((part) => part.type === 'dayPeriod')?.value ?? ''
  return { time, period }
}

// "2:00 – 3:00 PM" (the shared day period is only printed once when it matches)
export const formatTimeRange = (start: string, end: string): string => {
  const startParts = formatTimeParts(new Date(start))
  const endParts = formatTimeParts(new Date(end))
  const endFormatted = `${endParts.time} ${endParts.period}`
  return startParts.period === endParts.period ? `${startParts.time} – ${endFormatted}` : `${startParts.time} ${startParts.period} – ${endFormatted}`
}

// Inline date/time shown next to the calendar icon on Suggested rows: "Jul 1, 2026 · 10:00 – 11:00 AM".
// Falls back to a "no time" label when the suggested meeting has no start/end yet.
export const formatSuggestedTime = (start: string | undefined, end: string | undefined, noTimeLabel = 'No time discussed'): string =>
  start && end ? `${BOOKING_DATE_FORMATTER.format(new Date(start))} · ${formatTimeRange(start, end)}` : noTimeLabel

// "THU"
export const formatWeekdayShort = (date: Date): string => WEEKDAY_FORMATTER.format(date).toUpperCase()

// "9:00 AM" — a single clock time, joining the value parts so we never depend on the
// literal separator Intl emits (recent ICU uses a narrow no-break space).
export const formatTime = (date: Date): string => {
  const { time, period } = formatTimeParts(date)
  return `${time} ${period}`
}

// "Friday, June 21 · 9:00 AM" — the WHEN line of the calendar-invite preview
export const formatInviteWhen = (start: string): string => {
  const date = new Date(start)
  return `${INVITE_DATE_FORMATTER.format(date)} · ${formatTime(date)}`
}

const LOCATION_KIND_LABELS: Partial<Record<LocationKind, string>> = {
  [LocationKind.ACCOUNT_DEFAULT]: 'Account default',
  [LocationKind.USER_DEFAULT]: 'User default',
}

export const getBookingLocationLine = (booking: Booking, location: Location | undefined): string | undefined => {
  if (booking.meetingMode !== MeetingMode.IN_PERSON) {
    return undefined
  }
  const kindLabel = booking.location ? LOCATION_KIND_LABELS[booking.location.kind] : undefined
  const isUnconfirmed = booking.status !== BookingStatus.CONFIRMED && booking.status !== BookingStatus.CANCELLED
  if (kindLabel && isUnconfirmed) {
    return location ? `${kindLabel} · ${location.name}` : kindLabel
  }
  return location?.address
}

const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000

const calendarDayDiff = (from: Date, to: Date): number => {
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate())
  return Math.round((toDay.getTime() - fromDay.getTime()) / MILLISECONDS_IN_A_DAY)
}

// "SENT 13 DAYS AGO" / "SENT TODAY" labels for Waiting rows
export const getSentAgoLabel = (sentAt: string, now: Date = new Date()): string => {
  const daysAgo = calendarDayDiff(new Date(sentAt), now)
  if (daysAgo <= 0) {
    return 'SENT TODAY'
  }
  if (daysAgo === 1) {
    return 'SENT YESTERDAY'
  }
  return `SENT ${daysAgo} DAYS AGO`
}

const formatDaysUntil = (date: Date, now: Date): string => {
  const days = calendarDayDiff(now, date)
  if (days <= 0) {
    return 'today'
  }
  if (days === 1) {
    return 'tomorrow'
  }
  return `in ${days} days`
}

export interface ReminderPill {
  label: string
  // 'last' is the exhausted-schedule red pill; 'sent' is the lavender in-progress pill
  tone: 'sent' | 'last'
}

export interface ReminderStatus {
  // colored pill shown once the client has been reminded on the automatic schedule
  pill?: ReminderPill
  // gray follow-on text rendered next to a pill, e.g. "Next: in 2 days"
  detail?: string
  // gray standalone text shown instead of a pill (manual schedule, or the first upcoming reminder)
  plain?: string
}

// The reminder-status variants of the Waiting rows: a colored pill once reminders are
// under way (optionally with a "Next: …" detail), or plain gray text for the manual / first-up cases.
export const getReminderStatus = (reminder: ReminderInfo | undefined, now: Date = new Date()): ReminderStatus | null => {
  if (!reminder) {
    return null
  }
  const count = reminder.count ?? 0
  if (!reminder.automated) {
    return { plain: count > 0 ? 'No automated reminders – sent manually' : 'No automated reminders' }
  }
  if (reminder.nextSendAt) {
    const next = formatDaysUntil(new Date(reminder.nextSendAt), now)
    if (count === 0) {
      return { plain: `Reminder: ${next.charAt(0).toUpperCase()}${next.slice(1)}` }
    }
    const plural = count === 1 ? 'reminder' : 'reminders'
    return { pill: { label: `${count} ${plural} sent`, tone: 'sent' }, detail: `Next: ${next}` }
  }
  if (count > 0) {
    return { pill: { label: 'Last reminder sent', tone: 'last' } }
  }
  return { plain: 'No reminders scheduled' }
}
